import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Drawer,
  Button,
  Typography,
  Box,
  IconButton,
  Checkbox,
  FormControlLabel,
  TextField,
  InputAdornment,
} from "@mui/material";
import {
  Close as CloseIcon,
  Search as SearchIcon,
  DragIndicator as DragIndicatorIcon,
} from "@mui/icons-material";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import "./styles.css";

const COLUMN_DISPLAY_NAMES = {
  leaseEventId: "TME Event Id",
  eventId: "Event Id",
  leaseCode: "Tenure Id",
  leaseName: "Lease Name",
  propertyName: "Property Name",
  streetAddress1: "Property Address",
  businessUnit: "Business Unit",
  countryName: "Country",
  stateProvince: "State / Province",
  city: "City",
  rentableBuildingArea: "Building Area",
  eventType: "Event Type",
  leaseEventStatusName: "Event Status",
  eventNote: "Event Note",
  tenureType: "Tenure Type",
  lastComment: "Last Comment",
  lastCommentCreatedDate: "Last Comment Date",
  noticeDate: "Notice Date",
  actionDate: "Event Date",
  expireDate: "Lease End Date",
  snoozedUntil: "Snoozed Until",
  snoozedComment: "Snoozed Comment",
  leaseEventIgnoredDate: "Ignored Date",
  ignoreComment: "Ignore Comment",
  transactions: "Associated Transactions",
};

const ColumnVisibilityModal = ({
  open,
  onClose,
  columnSettings = [],
  defaultColumnVisibilityModel = {},
  onColumnVisibilityChange,
  onColumnOrderChange,
  onReset,
  excludeColumns = [],
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [columns, setColumns] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const isDraggingRef = useRef(false);

  const getDisplayName = useCallback((columnField) => {
    return COLUMN_DISPLAY_NAMES[columnField] || columnField;
  }, []);

  useEffect(() => {
    if (open && defaultColumnVisibilityModel && !isInitialized && !isDraggingRef.current) {
      const columnSettingsMap = new Map();
      if (columnSettings && columnSettings.length > 0) {
        columnSettings.forEach((col) => {
          if (col.column !== "actions") {
            columnSettingsMap.set(col.column, col);
          }
        });
      }

      const allColumnKeys = Object.keys(defaultColumnVisibilityModel);
      const baseColumns = allColumnKeys
        .map((columnKey, gridIndex) => ({ columnKey, gridIndex }))
        .filter(({ columnKey }) => columnKey !== "actions")
        .filter(({ columnKey }) => !excludeColumns.includes(columnKey))
        .map(({ columnKey, gridIndex }) => {
          const existingSetting = columnSettingsMap.get(columnKey);
          if (existingSetting) {
            return {
              column: columnKey,
              order: existingSetting.order != null ? existingSetting.order : gridIndex,
              defaultOrder: gridIndex,
              isVisible:
                existingSetting.isVisible !== null && existingSetting.isVisible !== undefined
                  ? existingSetting.isVisible
                  : defaultColumnVisibilityModel[columnKey],
            };
          }
          return {
            column: columnKey,
            order: gridIndex,
            defaultOrder: gridIndex,
            isVisible: defaultColumnVisibilityModel[columnKey],
          };
        });

      const sortedByOrder = [...baseColumns].sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
      /* Fixed order: eventId position 0, leaseName position 1; rest follow saved order */
      const pinnedSlots = [null, null];
      const regularColumns = [];
      sortedByOrder.forEach((col) => {
        if (col.column === "eventId") pinnedSlots[0] = col;
        else if (col.column === "leaseName") pinnedSlots[1] = col;
        else regularColumns.push(col);
      });
      const sortedColumns = [...pinnedSlots.filter(Boolean), ...regularColumns];
      sortedColumns.forEach((col, index) => {
        col.order = index;
      });
      setColumns(sortedColumns);
      setIsInitialized(true);
    }
  }, [open, columnSettings, defaultColumnVisibilityModel, excludeColumns, isInitialized]);

  useEffect(() => {
    if (!open) {
      setIsInitialized(false);
      setSearchTerm("");
      isDraggingRef.current = false;
    }
  }, [open]);

  const filteredColumns = useMemo(() => {
    if (!searchTerm.trim()) return columns;
    return columns.filter((column) => {
      const displayName = getDisplayName(column.column);
      return displayName.toLowerCase().includes(searchTerm.toLowerCase());
    });
  }, [columns, searchTerm, getDisplayName]);

  const nonHideableColumnsList = ["eventId", "leaseName", "tenureType"];
  /* eventId and leaseName are fixed left-pinned; order not changeable */
  const pinnedColumnsList = ["eventId", "leaseName"];

  const { showHideAll, showHideAllIndeterminate, isResetDisabled } = useMemo(() => {
    const modifiable = columns.filter((col) => !nonHideableColumnsList.includes(col.column));
    const visibleCount = modifiable.filter((col) => col.isVisible).length;
    const showAll = visibleCount === modifiable.length;
    const indeterminate = visibleCount > 0 && visibleCount < modifiable.length;
    const hasChanges = columns.some((col) => {
      const defaultVisibility = defaultColumnVisibilityModel[col.column] ?? false;
      const isNonHideable = nonHideableColumnsList.includes(col.column);
      const expectedVisibility = isNonHideable ? true : defaultVisibility;
      return col.isVisible !== expectedVisibility;
    });
    return {
      showHideAll: showAll,
      showHideAllIndeterminate: indeterminate,
      isResetDisabled: !hasChanges,
    };
  }, [columns, defaultColumnVisibilityModel]);

  const handleColumnToggle = useCallback(
    (columnField) => {
      const updatedColumns = columns.map((col) =>
        col.column === columnField ? { ...col, isVisible: !col.isVisible } : col
      );
      setColumns(updatedColumns);
      if (onColumnVisibilityChange) {
        const changes = {};
        changes[columnField] = !columns.find((col) => col.column === columnField).isVisible;
        onColumnVisibilityChange(changes);
      }
    },
    [columns, onColumnVisibilityChange]
  );

  const handleShowHideAll = useCallback(() => {
    const newVisibility = showHideAllIndeterminate || !showHideAll;
    const updatedColumns = columns.map((col) => ({
      ...col,
      isVisible: nonHideableColumnsList.includes(col.column) ? true : newVisibility,
    }));
    setColumns(updatedColumns);
    if (onColumnVisibilityChange) {
      const changes = {};
      updatedColumns.forEach((col) => {
        changes[col.column] = col.isVisible;
      });
      onColumnVisibilityChange(changes);
    }
  }, [columns, showHideAll, showHideAllIndeterminate, onColumnVisibilityChange]);

  const handleReset = useCallback(() => {
    const resetColumns = columns.map((col) => {
      if (nonHideableColumnsList.includes(col.column)) {
        return { ...col, isVisible: true, order: col.defaultOrder ?? col.order };
      }
      const defaultVisibility = defaultColumnVisibilityModel[col.column] ?? false;
      return { ...col, isVisible: defaultVisibility, order: col.defaultOrder ?? col.order };
    });
    const sortedByOrder = [...resetColumns].sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
    const pinnedSlots = [null, null];
    const regularColumns = [];
    sortedByOrder.forEach((col) => {
      if (col.column === "eventId") pinnedSlots[0] = col;
      else if (col.column === "leaseName") pinnedSlots[1] = col;
      else regularColumns.push(col);
    });
    const sortedResetColumns = [...pinnedSlots.filter(Boolean), ...regularColumns];
    sortedResetColumns.forEach((col, index) => {
      col.order = index;
    });
    setColumns(sortedResetColumns);
    if (onColumnVisibilityChange) {
      const changes = {};
      sortedResetColumns.forEach((col) => {
        changes[col.column] = col.isVisible;
      });
      onColumnVisibilityChange(changes);
    }
    if (onReset) {
      setTimeout(() => onReset(sortedResetColumns), 100);
    }
  }, [columns, defaultColumnVisibilityModel, onColumnVisibilityChange, onReset]);

  const handleDragStart = useCallback(() => {
    setIsDragging(true);
    isDraggingRef.current = true;
  }, []);

  const handleDragEnd = useCallback(
    (result) => {
      setIsDragging(false);
      isDraggingRef.current = false;
      if (!result.destination || result.source.index === result.destination.index) return;
      if (searchTerm.trim() !== "") return;
      /* eventId and leaseName are fixed at 0 and 1; do not allow moving any column to/from those positions */
      if (result.source.index < 2 || result.destination.index < 2) return;
      const reorderedColumns = Array.from(columns);
      const [movedColumn] = reorderedColumns.splice(result.source.index, 1);
      reorderedColumns.splice(result.destination.index, 0, movedColumn);
      reorderedColumns.forEach((col, index) => {
        col.order = index;
      });
      setColumns(reorderedColumns);
      if (onColumnOrderChange) {
        const mockGridColumns = reorderedColumns.map((col) => ({ field: col.column }));
        onColumnOrderChange(
          {
            field: movedColumn.column,
            targetIndex: result.destination.index,
            oldIndex: result.source.index,
          },
          mockGridColumns
        );
      }
    },
    [columns, searchTerm, onColumnOrderChange]
  );

  const handleModalClose = () => {
    onClose();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={() => {}}
      className="lease-events-columns-visibility-modal"
      sx={{
        "& .MuiDrawer-paper": {
          width: "360px",
          maxWidth: "360px",
          height: "100vh",
          position: "fixed",
          top: 0,
          right: 0,
          zIndex: 1300,
        },
        "& .MuiBackdrop-root": {
          backgroundColor: "rgba(51, 68, 74, 0.30)",
          pointerEvents: "none",
        },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100vh" }}>
        <Box
          className="lease-events-column-modal-header"
          sx={{ p: 2, borderBottom: "1px solid #E5E7EB", flexShrink: 0 }}
        >
          <Box className="lease-events-column-modal-title-section">
            <Typography
              variant="h6"
              className="lease-events-column-modal-title"
              sx={{ fontFamily: "'Source Sans Pro'" }}
            >
              Columns
            </Typography>
            <IconButton onClick={handleModalClose} className="lease-events-column-modal-close-button" size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <Box className="lease-events-column-modal-search-section">
            <TextField
              fullWidth
              size="small"
              placeholder="Search by column name"
              variant="outlined"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="lease-events-column-modal-search-input"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon className="lease-events-column-modal-search-icon" fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment:
                  searchTerm && (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setSearchTerm("")}
                        size="small"
                        className="lease-events-column-modal-search-clear"
                      >
                        <CancelOutlinedIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
              }}
            />
          </Box>

          <Box className="lease-events-column-modal-show-hide-section">
            <FormControlLabel
              control={
                <Checkbox
                  checked={showHideAll}
                  indeterminate={showHideAllIndeterminate}
                  onChange={handleShowHideAll}
                  size="small"
                  className="lease-events-column-modal-show-hide-checkbox"
                />
              }
              label="Show/hide all"
              className="lease-events-column-modal-show-hide-label"
              sx={{ "& .MuiFormControlLabel-label": { fontFamily: "'Source Sans Pro'" } }}
            />
            <Button
              onClick={handleReset}
              variant="outlined"
              size="small"
              startIcon={<RestartAltIcon />}
              className="lease-events-column-modal-reset-button"
              disabled={isResetDisabled}
              sx={{ fontFamily: "'Source Sans Pro'" }}
            >
              Reset
            </Button>
          </Box>
        </Box>

        <Box
          className={`lease-events-column-modal-content ${isDragging ? "dragging" : ""}`}
          sx={{ p: 2, flex: 1, minHeight: 0 }}
        >
          <DragDropContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
            <Droppable droppableId="lease-events-column-list">
              {(provided) => (
                <div
                  className="lease-events-column-modal-list"
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  style={{ overflowY: "auto", padding: 0, scrollBehavior: "unset" }}
                >
                  {filteredColumns.length === 0 ? (
                    <Box className="lease-events-column-modal-no-results">
                      <Typography 
                        sx={{ 
                          fontFamily: "'Source Sans Pro'",
                          fontSize: "14px !important",
                          fontWeight: "400 !important"
                        }}
                      >
                        No columns found
                      </Typography>
                    </Box>
                  ) : (
                    filteredColumns.map((column, index) => {
                      const displayName = getDisplayName(column.column);
                      const isNonHideable = nonHideableColumnsList.includes(column.column);
                      const isPinned = pinnedColumnsList.includes(column.column);
                      const isDragDisabled = isPinned || searchTerm.trim() !== "";

                      return (
                        <Draggable
                          key={column.column}
                          draggableId={column.column}
                          index={index}
                          isDragDisabled={isDragDisabled}
                        >
                          {(provided, snapshot) => (
                            <Box
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              className={`lease-events-column-modal-item ${
                                snapshot.isDragging ? "lease-events-column-modal-item-dragging" : ""
                              } ${isDragging ? "lease-events-column-modal-item-drag-active" : ""}`}
                              style={{
                                ...provided.draggableProps.style,
                                ...(snapshot.isDragging && {
                                  transform: provided.draggableProps.style?.transform,
                                  zIndex: 1000,
                                }),
                              }}
                            >
                              {!isPinned && (
                                <IconButton
                                  {...provided.dragHandleProps}
                                  size="small"
                                  className={`lease-events-column-modal-drag-handle ${isDragDisabled ? "disabled" : ""}`}
                                >
                                  <DragIndicatorIcon fontSize="small" />
                                </IconButton>
                              )}
                              <Checkbox
                                checked={column.isVisible}
                                onChange={isNonHideable ? undefined : () => handleColumnToggle(column.column)}
                                disabled={isNonHideable}
                                size="small"
                                className="lease-events-column-modal-column-checkbox"
                                style={isPinned ? { marginLeft: "25px" } : {}}
                              />
                              <Typography
                                variant="body2"
                                className={`lease-events-column-modal-column-text ${isNonHideable ? "non-hideable" : ""}`}
                                sx={{
                                  fontSize: "14px !important",
                                  fontWeight: "400 !important",
                                  fontFamily: "'Source Sans Pro' !important"
                                }}
                              >
                                {displayName}
                              </Typography>
                            </Box>
                          )}
                        </Draggable>
                      );
                    })
                  )}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </Box>
      </Box>
    </Drawer>
  );
};

export default React.memo(ColumnVisibilityModal);
